import { isProxy, toRaw } from 'vue';

/** True when stored control ref is the same instance as ctx (Vue proxies / remount races). */
export function isSameControlRef(stored, ctx) {
    if (stored == null || ctx == null)
        return false;
    if (stored === ctx)
        return true;

    const a = isProxy(stored) ? toRaw(stored) : stored;
    const b = isProxy(ctx) ? toRaw(ctx) : ctx;
    if (a === b)
        return true;

    // Prefer stable Vue instance identity when both sides are component proxies.
    if (a?.$?.uid != null && a.$.uid === b?.$?.uid)
        return true;

    return false;
}

export function resolveDialogRegistry(ctx) {
    if (!ctx)
        return {};

    if (ctx.dialogScopeRegistry)
        return ctx.dialogScopeRegistry;

    if (ctx.dialogScopeitems)
        return ctx.dialogScopeitems;

    const dialogName = ctx.dialogName;
    if (dialogName && ctx.scopeitems && ctx.scopeitems[dialogName])
        return ctx.scopeitems[dialogName];

    return {};
}

export function applyDialogScopeFromControl(actionContext, control) {
    if (!actionContext || !control)
        return;

    actionContext.dialogScopeRegistry = control.dialogScopeRegistry ?? null;
    actionContext.dialogScopeitems = control.dialogScopeitems ?? null;
    actionContext.dialogName = control.dialogName ?? null;
    actionContext.dialogNamed = control.dialogNamed ?? false;
    actionContext.Dialog = resolveDialogRegistry(actionContext);
}

export function ensureActionContextDialogScope(context) {
    if (!context || typeof context !== 'object')
        return;

    // Read from the raw object so Vue component proxies do not warn about undefined $origin during render.
    const raw = isProxy(context) ? toRaw(context) : context;
    const origin = raw.$origin;
    if (typeof origin === 'string' && origin.includes('compileAction') && raw.$parent)
        applyDialogScopeFromControl(context, raw.$parent);
}

export function registerDialogControl(ctx) {
    if (!ctx?.controlData?.Name)
        return;

    const name = ctx.controlData.Name;
    const dialogRegistry = ctx.dialogScopeRegistry || ctx.dialogScopeitems;
    const isNamed = Boolean(ctx.dialogNamed);

    if (dialogRegistry) {
        dialogRegistry[name] = ctx;
        if (!isNamed && ctx.scopeitems)
            ctx.scopeitems[name] = ctx;
    }
    else if (ctx.scopeitems)
        ctx.scopeitems[name] = ctx;
}

export function unregisterDialogControl(ctx) {
    if (!ctx?.controlData?.Name)
        return;

    const name = ctx.controlData.Name;
    const dialogRegistry = ctx.dialogScopeRegistry || ctx.dialogScopeitems;
    const isNamed = Boolean(ctx.dialogNamed);

    // Only clear if this instance still owns the slot. Vue 3 keyed remounts often
    // create the replacement before unmounting the old one; a blind delete would
    // wipe Control.<Name> / Dialog.<Name> for the live control (e.g. FastGrid ERR
    // after RefreshIconActions remounts a VerticalLayout).
    if (dialogRegistry && isSameControlRef(dialogRegistry[name], ctx))
        delete dialogRegistry[name];

    if (!isNamed && ctx.scopeitems && isSameControlRef(ctx.scopeitems[name], ctx))
        delete ctx.scopeitems[name];
}
